import { useEffect, useState } from "react"
import type { ReactNode } from "react"
import ArtworkLoupe from "components/ArtworkLoupe"
import LoadableImage from "components/LoadableImage"

interface Props {
  src: string
  fallbackSrcs?: string[]
  fallback?: ReactNode
  alt?: string
  width: number
  height: number
  zoom?: number
  /** Disable the loupe (e.g. on touch devices where the frame is a nav link). */
  enabled?: boolean
  eager?: boolean
}

/**
 * Static image displayed with the shared ambient loupe. Delegates the
 * hover/touch interaction + edge-aware positioning to ArtworkLoupe so every
 * art surface (generative tokens and 1/1 images alike) behaves identically.
 */
const AmbientZoomImage = ({
  src,
  fallbackSrcs = [],
  fallback,
  alt,
  width,
  height,
  zoom = 3,
  enabled = true,
  eager = false,
}: Props) => {
  const [resolvedSrc, setResolvedSrc] = useState(src)

  useEffect(() => {
    setResolvedSrc(src)
  }, [src])

  const renderContent = (contentWidth: number, contentHeight: number, isLoupe: boolean) =>
    isLoupe ? (
      <img
        src={resolvedSrc}
        alt=""
        aria-hidden="true"
        width={contentWidth}
        height={contentHeight}
        className="block h-full w-full object-contain"
        style={{ display: "block" }}
      />
    ) : (
      <LoadableImage
        src={src}
        fallbackSrcs={fallbackSrcs}
        fallback={fallback}
        alt={alt ?? ""}
        wrapperClassName="h-full w-full"
        className="h-full w-full object-contain"
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        loaderLabel="Loading artwork"
        onLoad={(event) => setResolvedSrc(event.currentTarget.currentSrc || event.currentTarget.src)}
      />
    )

  return (
    <div
      style={{ width, height }}
      className="flex items-center justify-center bg-gallery-surface"
    >
      <ArtworkLoupe
        active={enabled}
        width={width}
        height={height}
        zoom={zoom}
        renderContent={renderContent}
      />
    </div>
  )
}

export default AmbientZoomImage
